import format from 'date-fns/format';
import { ComponentProps, useMemo } from 'react';
import { DayPicker, MonthsProps, useDayPicker } from 'react-day-picker';

import { Icon } from './Icon';
import { legacyTextStyles } from './LegacyTextStyles';
import { newButtonStyles } from './NewButton';
import { cn } from './cn';

type CalendarProps = ComponentProps<typeof DayPicker> & {
  footer?: React.ReactNode;
};

export const Calendar = ({
  className,
  classNames: __classNames,
  showOutsideDays = true,
  footer,
  ...props
}: CalendarProps) => {
  const hasSelectedRange = Boolean(
    props.mode === 'range' && props.selected?.from && props.selected?.to,
  );

  const cellClassName = useMemo(() => {
    return cn(
      // Center text, set cell height and padding, vertical align
      'text-center h-8 py-0 px-1 my-auto relative',
      // Focused cell brought above others
      'focus-within:relative focus-within:z-20',
      // only apply range background styles if there is a selected range
      hasSelectedRange && [
        // TODO(chris)[ELU-5054]: This could be difficult to maintain in the future. See if there's a better way to handle multiple classes being applied to the same day.
        // apply right-half background to start of range cell
        '[&:has(.range-start):not(:has(.invisible))]:bg-[linear-gradient(to_right,transparent_50%,hsl(var(--color-bg-quaternary))_50%)]',
        // apply left-half background to end of range cell
        '[&:has(.range-end):not(:has(.invisible))]:bg-[linear-gradient(to_left,transparent_50%,hsl(var(--color-bg-quaternary))_50%)]',
        // apply background to middle of the range cells
        '[&:has([aria-selected]):not(:has(.range-start)):not(:has(.range-end)):not(:has(.invisible))]:bg-bg-quaternary',
      ],
    );
  }, [hasSelectedRange]);

  const dayTodayClassName = useMemo(() => {
    const isRange = props.mode === 'range';

    // eslint-disable-next-line tailwindcss/no-custom-classname
    return cn(
      isRange
        ? `[&:not(.range-start):not(.range-end)]:!bg-text-supporting`
        : `[&:not(.day-selected)]:!bg-text-supporting`,
      isRange
        ? `[&:not(.range-start):not(.range-end)]:hover:!bg-text-supporting`
        : `[&:not(.day-selected)]:hover:!bg-text-supporting`,
      '!text-bg-tertiary',
      // custom class name that allows us to target the today's cell
      'day-today',
    );
  }, [props.mode]);

  return (
    <DayPicker
      // always set this to true, otherwise, the empty cells don't render properly
      // we hide outside days with the day_outside class
      showOutsideDays={true}
      className={cn('p-4', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center h-8 relative items-center',
        caption_label: legacyTextStyles.body({
          color: 'tertiary',
          size: 'm',
          weight: 'bold',
        }),
        nav: 'space-x-1 flex items-center',
        nav_button: newButtonStyles({
          iconOnly: true,
          spacing: 'compact',
          type: 'ghost',
        }).button(),
        nav_button_previous: '!absolute left-1',
        nav_button_next: '!absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex mb-2',
        head_cell: legacyTextStyles.body({
          className: 'w-10 p-0 uppercase',
          color: 'supporting',
          size: 's',
          weight: 'bold',
        }),
        row: 'flex w-full h-10',
        cell: cellClassName,
        day: legacyTextStyles.body({
          className: 'size-8 rounded-lg hover:bg-bg-quaternary',
          color: 'secondary',
          size: 'm',
          weight: 'semibold',
        }),
        day_selected: '!bg-text-primary !text-bg-tertiary day-selected',
        day_today: dayTodayClassName,
        day_outside: `!text-text-disabled ${!showOutsideDays ? 'invisible' : ''}`,
        day_disabled: '!text-text-disabled',
        day_range_start: '!bg-text-primary !text-bg-tertiary range-start',
        day_range_end: '!bg-text-primary !text-bg-tertiary range-end',
        day_range_middle: cn(
          // don't override day_today styles if the day is in the middle of a range
          'aria-selected:[&:not(.day-today)]:!bg-bg-quaternary',
          'aria-selected:hover:[&:not(.day-today)]:!bg-border-secondary',
          'aria-selected:[&:not(.day-today)]:!text-text-primary',
        ),
        day_hidden: 'invisible',
      }}
      components={{
        IconLeft: () => <Icon name="ChevronLeft" size="xs" />,
        IconRight: () => <Icon name="ChevronRight" size="xs" />,
        Months: ({ children }: MonthsProps) => {
          const { classNames, styles } = useDayPicker();

          return (
            <div className="flex flex-col">
              <div className={classNames.months} style={styles.months}>
                {children}
              </div>
              {footer && (
                <>
                  <div className="my-3 h-px w-full bg-border-primary" />
                  {footer}
                </>
              )}
            </div>
          );
        },
      }}
      formatters={{
        formatWeekdayName: (date, options) => format(date, 'ccc', options),
      }}
      {...props}
    />
  );
};
